/***
 *
 *   ONBOARDING
 *   Phase 13 — step bodies restyled to match signup.tsx chrome. Keeps live
 *   APIs, step list, and /api/account/step progression.
 *
 **********/

import React, { useContext, useEffect, useState } from 'react';
import { Onboarding, Form, Message, cn } from 'components/lib';
import { AuthContext } from 'app/auth';
import { useTranslate } from 'app/translations';
import { useApiContext } from 'app/api';
import { useQuery, useMutation } from 'react-query';
import { ProjectForm } from 'components/form/template/project';
import Style from './onboarding.tailwind';
import settings from '../../settings';

const { client_url_main } = settings;

export function OnboardingView(props) {
    const context = useContext(AuthContext);
    const { callApi } = useApiContext();

    const user = useQuery('user', () => callApi({ url: '/api/user' }));
    const account = user.data?.data.accounts.find(
        (account) => account?.id === user?.data.data.account_id,
    );

    const { _t } = useTranslate();

    const views = [
        {
            key: 'welcome',
            name: _t('onboarding.start.title'),
            description: _t('onboarding.welcome', {
                name: context.user.first_name,
            }),
            icon: 'user',
            component: Welcome,
            isComplete: true,
        },
    ];

    if (context.permission.user && !context.permission.owner) {
        addView({
            key: 'contact',
            name: _t('setup.label'),
            description: _t('onboarding.contact.desc'),
            icon: 'user',
            component: SetupContact,
            isComplete: false,
        });
    }

    if (context.permission.owner && account) {
        addView({
            key: 'organization',
            name: _t('setup.label'),
            description: _t('organization.setup'),
            icon: 'home',
            component: SetupOrganization,
            isComplete: false,
        });

        addView({
            key: 'integration',
            name: _t('service.link.label'),
            description: _t('service.link.description'),
            icon: 'link',
            component: SetupIntegration,
            isComplete: false,
            skippable: true,
        });

        addView({
            key: 'project',
            name: _t('project.label.one'),
            description: _t('project.configure'),
            icon: 'folder',
            component: SetupProject,
            isComplete: false,
        });

        addView({
            key: 'invite',
            name: _t('onboarding.invite.title'),
            description: _t('onboarding.invite.desc'),
            icon: 'user-plus',
            component: InviteUsers,
            isComplete: true,
            skippable: true,
        });

        addView({
            key: 'done',
            name: _t('onboarding.done.title'),
            description: _t('onboarding.done.desc'),
            icon: 'check',
            component: Done,
            isComplete: true,
        });
    }

    if (context.user.duplicate_user) {
        views.unshift({
            key: 'duplicate',
            name: _t('onboarding.important'),
            description: '',
            icon: 'alert-circle',
            component: DuplicateUser,
            isComplete: true,
        });
    }

    function addView(view) {
        views.push({
            ...view,
            isComplete: view.isComplete || account?.setup_step > views.length - 1,
        });
    }

    return <Onboarding save onFinish="/app/tickets" views={views} />;
}

function DuplicateUser() {
    const { _t } = useTranslate();
    return <Message type="warning" title={_t('user.exist')} text={_t('user.existDescription')} />;
}

function Welcome() {
    const { _t } = useTranslate();
    return (
        <p className="text-sm text-text-secondary">{_t('onboarding.welcomeBody')}</p>
    );
}

function Done() {
    return null;
}

function InviteUsers(props) {
    const { _t } = useTranslate();
    const user = useQuery('user');
    const defaultProject = user.data?.data.default_project;
    return (
        <Form
            data={{
                email: {
                    label: _t('email.label'),
                    type: 'email',
                    required: true,
                },
            }}
            buttonText={_t('invite.send')}
            url="/api/invite?target=user"
            injectedData={{
                project_id: defaultProject,
            }}
            callback={() => {
                localStorage.removeItem('project_id');
                props.next();
            }}
            method="POST"
        />
    );
}

function SetupContact(props) {
    const { _t } = useTranslate();
    const formData = {
        phone: {
            label: _t('phone.label'),
            type: 'phone',
            required: false,
        },
        position: {
            label: _t('table.position'),
            type: 'text',
            required: false,
            placeholder: _t('onboarding.contact.positionPlaceholder'),
        },
    };

    return (
        <Form
            data={formData}
            callback={(res) => {
                if (res.status === 200) {
                    props.finish();
                }
            }}
            buttonText={_t('save')}
            url="/api/user"
            method="PATCH"
        />
    );
}

function SetupIntegration(props) {
    const { _t } = useTranslate();
    const { callApi } = useApiContext();
    const [selected, setSelected] = useState(null);
    const [connecting, setConnecting] = useState(false);

    const query = useQuery('accountIntegrations', () =>
        callApi({ url: '/api/account/integrations' }),
    );

    const connected = (query.data?.data?.length || 0) > 0;

    useEffect(() => {
        if (connected && !props.isComplete) {
            props.complete();
        }
        // Intentionally omit props.complete — stable enough for step advance once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connected, props.isComplete]);

    async function handleConnect() {
        if (!selected || connecting) return;
        setConnecting(true);
        try {
            const res = await callApi({
                url: '/api/integration/connect',
                method: 'POST',
                data: { service: selected },
                headers: {
                    'Redirect-Url': window.location.href,
                },
            });
            if (res?.data?.redirect) {
                window.location.href = res.data.redirect;
            }
        } finally {
            setConnecting(false);
        }
    }

    if (query.isLoading) return null;

    if (connected) {
        return (
            <p className="text-sm text-success">{_t('integration.connected')}</p>
        );
    }

    // Live catalog is ClickUp only — reference IntegrationStep card shape.
    const services = [{ service_name: 'clickup', category: 'pm' }];

    return (
        <div className="space-y-5">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {services.map((entry) => {
                    const isSelected = selected === entry.service_name;
                    return (
                        <button
                            key={entry.service_name}
                            type="button"
                            disabled={connecting}
                            onClick={() => setSelected(entry.service_name)}
                            className={cn(
                                'flex flex-col items-start gap-1.5 rounded-lg border p-2.5 text-left transition-colors',
                                isSelected
                                    ? 'border-primary-600 bg-primary-50 ring-1 ring-primary-600 dark:bg-primary-900/20'
                                    : 'cursor-pointer border-border bg-surface hover:border-primary-300 hover:bg-surface-elevated',
                            )}>
                            <span className="text-xs font-semibold capitalize text-text-primary">
                                {entry.service_name}
                            </span>
                            <span className="text-[10px] capitalize text-text-muted">
                                {entry.category === 'pm'
                                    ? _t('onboarding.integration.categoryPm')
                                    : _t('onboarding.integration.categoryChat')}
                            </span>
                        </button>
                    );
                })}
            </div>

            <button
                type="button"
                disabled={!selected || connecting}
                onClick={handleConnect}
                className={cn(
                    'inline-flex h-10 w-full items-center justify-center gap-2 rounded-md px-4 text-sm font-medium transition-colors',
                    !selected || connecting
                        ? 'cursor-not-allowed bg-primary-600/50 text-white'
                        : 'bg-primary-600 text-white hover:bg-primary-700',
                )}>
                {_t('integration.connect')}
            </button>
        </div>
    );
}

function SetupOrganization(props) {
    const context = useContext(AuthContext);
    const { _t } = useTranslate();
    const { account } = props;

    const formData = {
        logo: {
            label: _t('organization.logo'),
            type: 'logo',
            name: 'logo',
            required: false,
            placeholder: account.file_path,
        },
        name: {
            label: _t('organization.name'),
            type: 'text',
            required: true,
            placeholder: 'My organization',
            value: account.name,
        },
        domain: {
            label: _t('domain.label'),
            type: 'domain',
            required: true,
            value: account.domain,
            placeholder: `Ex: my-company-name`,
            domain: `.${client_url_main}`,
        },
    };

    return (
        <Form
            data={formData}
            callback={(res) => {
                if (res.status === 200) {
                    props.next();
                    if (res.data.domain !== account.domain) {
                        context.switchDomain(res.data.domain);
                    }
                    account.file_path = res.data.file_path;
                    context.update({ accounts: [account] });
                }
            }}
            buttonText={_t('save')}
            url="/api/account"
            method="PUT"
        />
    );
}

function SetupProject(props) {
    const { callApi } = useApiContext();
    const { _t } = useTranslate();
    const user = useQuery('user', () => callApi({ url: '/api/user' }));
    const defaultProject = user.data?.data.default_project;
    const [project, setProject] = useState(null);

    const { mutate: createDefaultProject } = useMutation(
        () =>
            callApi({
                url: '/api/project',
                method: 'POST',
                data: {
                    name: _t('project.defaultName'),
                    privacy: 'private',
                },
            }),
        {
            onSuccess: (res) => {
                if (res.status === 200) {
                    user.refetch();
                    localStorage.setItem('project_id', JSON.stringify(res.data.id));
                    setProject(res.data);
                }
            },
        },
    );

    useEffect(() => {
        if (!defaultProject) createDefaultProject();
    }, [createDefaultProject, defaultProject]);

    return (
        <div className={Style.setupProject}>
            {defaultProject && (
                <ProjectForm
                    method="PATCH"
                    url={`/api/project/${defaultProject}`}
                    id={defaultProject}
                    disableTitle
                    providedProject={project}
                    callback={() => {
                        props.next();
                    }}
                />
            )}
        </div>
    );
}
